import React, { useEffect } from 'react';
import PageTitle from '../../components/common/PageTitle';
import SertificateCard from '../../components/cards/SertificateCard';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useDispatch, useSelector } from 'react-redux';
import { getAbout } from '../../services/actions/MainAction';
import { 
  Award, 
  Users, 
  Clock, 
  Shield,
  Target,
  TrendingUp,
  Zap,
  Sparkles,
  ChevronRight,
  Building,
  Lightbulb
} from 'lucide-react';

function About() {
  const contentData = useSelector(state => state.Data.contentData);
  const aboutData = useSelector(state => state.Data.aboutData);
  const lang_id = useSelector(state => state.Data.lang_id);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAbout(lang_id));
    window.scrollTo(0, 0);
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    });
  }, [lang_id, dispatch]);

  // Stats data
  const stats = [
    { icon: <Clock className="w-6 h-6" />, value: "15+", label: "İllik Təcrübə", color: "blue" },
    { icon: <Award className="w-6 h-6" />, value: "500+", label: "Layihələr", color: "amber" },
    { icon: <Users className="w-6 h-6" />, value: "1000+", label: "Məmnun Müştəri", color: "emerald" },
    { icon: <Shield className="w-6 h-6" />, value: "24/7", label: "Xidmət", color: "purple" }
  ];

  // Mission & Vision
  const missionVision = [
    {
      icon: <Target className="w-8 h-8" />,
      title: contentData?.about_mission_title,
      description:contentData?.about_mission_desc,
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title:contentData?.about_vision_title,
      description: contentData?.about_vision_desc,
      color: "from-amber-500 to-amber-600"
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: contentData?.about_value_title,
      description: contentData?.about_value_desc,
      color: "from-emerald-500 to-emerald-600"
    }
  ];

  // Services preview


  return (
    <main className="flex-1 bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/4 left-10 w-64 h-64 border-2 border-blue-400/20 rounded-full"></div>
            <div className="absolute bottom-1/4 right-10 w-48 h-48 border-2 border-amber-400/20 rounded-full"></div>
          </div>
        </div>

        <div className="relative container mx-auto px-4 py-10 md:py-10">
          {/* Page Title with Enhanced Design */}
          <div className="text-center mb-0" data-aos="fade-up">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-blue-50 to-amber-50 rounded-full mb-6 border border-blue-100">
              <Sparkles className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-700">{contentData?.nav_1}</span>
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                {contentData?.nav_1 || "Şirkət"}
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              {contentData?.about_desc}
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {/* Main Content */}
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12 mb-20">
              {/* Left Column - Mission/Vision/Values */}
              <div className="space-y-8" data-aos="fade-right">
                {missionVision.map((item, index) => (
                  <div key={index} className="group" data-aos="fade-up" data-aos-delay={index * 100}>
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300">
                      <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4 shadow-md`}>
                        <div className="text-white">
                          {item.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      <div className={`h-1 w-12 bg-gradient-to-r ${item.color} mt-4 rounded-full transition-all duration-300 group-hover:w-16`}></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Center Column - Main Description */}
              <div className="lg:col-span-2" data-aos="fade-left">
                <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gray-100">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{contentData?.about_comp}</h2>
                      <p className="text-gray-500">{contentData?.about_comp_txt}</p>
                    </div>
                  </div>
                  
                  {/* About Content */}
                  <div className="prose prose-lg max-w-none">
                    <div 
                      dangerouslySetInnerHTML={{__html: aboutData?.translate?.description || ''}}
                      className="text-gray-700 leading-relaxed"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default About;